/**
 * useDisplay Composable
 *
 * Provides reactive display information, device detection, and responsive helpers
 * for consistent UI rendering across different viewport sizes and devices.
 *
 * Features:
 * - Reactive viewport size information
 * - Device type detection (mobile/desktop)
 * - React Native WebView detection
 * - Media query breakpoint helpers
 */

import { ref, computed, onMounted, onUnmounted } from "vue";
import { useNuxtApp } from "#app";

export function useDisplay() {
  // Initialize with conservative default (assume mobile first)
  const width = ref(typeof window !== "undefined" ? window.innerWidth : 0);
  const height = ref(typeof window !== "undefined" ? window.innerHeight : 0);

  // Breakpoint thresholds follow Tailwind defaults
  const BREAKPOINTS = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
  };

  /**
   * Updates dimensions on resize
   */
  const updateDimensions = () => {
    width.value = window.innerWidth;
    height.value = window.innerHeight;
  };

  // Set up and clean up resize listener when in browser context
  onMounted(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateDimensions);
      updateDimensions(); // Initial call
    }
  });

  onUnmounted(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", updateDimensions);
    }
  });

  /**
   * Indicates if the current viewport is mobile-sized
   * Uses md breakpoint (768px) as the threshold
   */
  const isMobile = computed(() => width.value < BREAKPOINTS.md);

  /**
   * Indicates if the current viewport is desktop-sized
   * Uses md breakpoint (768px) as the threshold
   */
  const isDesktop = computed(() => width.value >= BREAKPOINTS.md);

  /**
   * Detects if the application is running in a React Native WebView
   * by checking the user agent for React Native indicators
   */
  /**
   * Detects if the application is running in a React Native WebView or App
   * by checking:
   * 1. X-GO-Client header via the reactNative plugin
   * 2. User agent for React Native indicators
   */
  const isReactNativeWebView = computed(() => {
    // Try to get the Nuxt app instance for the reactNative plugin
    let reactNativePlugin = null;
    try {
      const nuxtApp = useNuxtApp();
      reactNativePlugin = nuxtApp.$reactNative;
    } catch (e) {
      // Plugin not available, continue with user agent detection
    }

    // Method 1: Check X-GO-Client header via plugin (most reliable)
    if (reactNativePlugin && reactNativePlugin.isApp === true) {
      return true;
    }

    // Method 2: Fall back to user agent detection if header check fails
    if (typeof window === "undefined" || !window.navigator) return false;

    // Check for React Native in user agent
    const ua = window.navigator.userAgent || "";
    return (
      ua.includes("ReactNativeWebView") ||
      // Additional check for query parameter for testing
      (typeof window.location !== "undefined" &&
        new URLSearchParams(window.location.search).has("platform") &&
        new URLSearchParams(window.location.search).get("platform") ===
          "react-native")
    );
  });

  /**
   * Checks if current viewport meets a specific breakpoint
   * @param breakpoint - Breakpoint to check against (sm, md, lg, xl, 2xl)
   * @returns boolean indicating if viewport width is at least the breakpoint width
   */
  const breakpoint = (bp: keyof typeof BREAKPOINTS) =>
    computed(() => {
      return width.value >= BREAKPOINTS[bp];
    });

  return {
    // Viewport dimensions
    width,
    height,

    // Device type
    isMobile,
    isDesktop,

    // Environment detection
    isReactNativeWebView,

    // Breakpoint helpers
    breakpoint,
    BREAKPOINTS,
  };
}

// Export as default as well for different import styles
export default useDisplay;
